export function integrationMarkup(
  element: HTMLElement | undefined,
  id: string,
) {
  const tag = element?.localName || "proof-form";
  const escape = (value: string) =>
    value
      .replaceAll("&", "&amp;")
      .replaceAll('"', "&quot;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;");
  const attributes = [
    "mode",
    "modal",
    "show-proof",
    "width",
    "height",
    "max-width",
    "max-height",
    "dialog-title",
    "trigger-label",
  ]
    .flatMap((key) =>
      element?.hasAttribute(key)
        ? [` ${key}="${escape(element.getAttribute(key) || "")}"`]
        : [],
    )
    .join("");
  return `<${tag} id="${id}"${attributes}></${tag}>`;
}
export function integrationModuleUrl(element?: { localName: string }) {
  const moduleName = (element?.localName || "proof-form")
    .replace(/^proof-/, "")
    .replace("reusable-form", "reusable-forms");
  const resource = performance.getEntriesByType("resource").find((entry) => {
    try {
      return new URL(entry.name).pathname.endsWith(
        `/plugins/${moduleName}.mjs`,
      );
    } catch {
      return false;
    }
  });
  const loader = Array.from(document.scripts).find((script) => {
    try {
      return /\/(proof-form|embed)\.(m?js)$/.test(new URL(script.src).pathname);
    } catch {
      return false;
    }
  });
  const candidate = resource
    ? new URL(resource.name)
    : loader
      ? new URL(`plugins/${moduleName}.mjs`, loader.src)
      : undefined;
  if (candidate) {
    candidate.username = "";
    candidate.password = "";
    candidate.search = "";
    candidate.hash = "";
    return candidate.href;
  }
  return `/plugins/${moduleName}.mjs`;
}
/** Developer-only overlay. The host still owns permissions and persistence. */
export function integrationDesignMode(
  element: HTMLElement & { options?: any },
) {
  const shadow = element.shadowRoot;
  if (!shadow) return;
  const enabled =
    element.hasAttribute("design-mode") || element.options?.designMode === true;
  let header = shadow.querySelector<HTMLElement>("[data-integration-design]");
  if (!enabled) {
    header?.remove();
    return;
  }
  if (header) return;
  header = document.createElement("header");
  header.dataset.integrationDesign = "";
  header.style.cssText = "position:absolute;right:8px;top:8px;z-index:20";
  const gear = document.createElement("button");
  gear.type = "button";
  gear.textContent = "⚙";
  gear.setAttribute("aria-label", "Configure integration");
  const dialog = document.createElement("dialog");
  dialog.style.cssText =
    "width:min(700px,90vw);max-height:85dvh;margin:auto;padding:24px;overflow:auto";
  const title = document.createElement("h2");
  title.textContent = "Integration design mode";
  dialog.append(title);
  const options = () => element.options || {};
  if (
    [
      "proof-authoring",
      "proof-form-configuration",
      "proof-esm-project",
      "proof-reusable-form",
    ].includes(element.localName)
  )
    for (const [key, label] of [
      ["allowProjectRename", "Allow project name editing"],
      ["enableStatements", "Enable statements"],
      ["enableRules", "Enable calculated rules and presets"],
      ["enableFormatting", "Enable formatting"],
      ["enableValidation", "Enable validation"],
      ["enableDynamicSources", "Enable dynamic data sources"],
      ["enableConditionalVisibility", "Enable conditional visibility"],
      ["enablePartOverrides", "Enable part overrides"],

      ["showHeading", "Show editor heading"],
      ["showDescription", "Show editor description"],
    ]) {
      const row = document.createElement("label");
      row.style.cssText = "display:block;padding:8px";
      const input = document.createElement("input");
      input.type = "checkbox";
      input.checked =
        options().editor?.[key] ??
        options().services?.editorPresentation?.[key] ??
        key.startsWith("enable");
      input.onchange = () => {
        const current = options();
        element.options =
          element.localName === "proof-authoring"
            ? {
                ...current,
                editor: { ...current.editor, [key]: input.checked },
              }
            : {
                ...current,
                services: {
                  ...current.services,
                  editorPresentation: {
                    ...current.services?.editorPresentation,
                    [key]: input.checked,
                  },
                },
              };
      };
      row.append(input, document.createTextNode(label));
      dialog.append(row);
    }
  for (const [key, value] of Object.entries(options()).filter(
    ([key, value]) =>
      ["string", "boolean", "number"].includes(typeof value) &&
      !/password|token|secret|credential|headers|authorization|cookie|key|url/i.test(
        key,
      ),
  )) {
    const label = document.createElement("label");
    label.style.cssText = "display:block;padding:8px";
    label.append(document.createTextNode(key.replace(/([A-Z])/g, " $1") + " "));
    const input = document.createElement("input");
    input.type =
      typeof value === "boolean"
        ? "checkbox"
        : typeof value === "number"
          ? "number"
          : "text";
    if (typeof value === "boolean") input.checked = value;
    else input.value = String(value);
    input.onchange = () => {
      element.options = {
        ...options(),
        [key]:
          typeof value === "boolean"
            ? input.checked
            : typeof value === "number"
              ? Number(input.value)
              : input.value,
      };
    };
    label.append(input);
    dialog.append(label);
  }
  const button = (label: string, action: () => void) => {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = label;
    b.onclick = action;
    dialog.append(b);
  };
  button("Reload preview", () => {
    element.options = options();
  });
  button("Generate source", () => {
    const source = document.createElement("dialog");
    source.style.cssText =
      "width:min(800px,90vw);max-height:85dvh;margin:auto;padding:24px";
    const text = document.createElement("textarea");
    text.readOnly = true;
    text.rows = 16;
    text.style.cssText = "width:100%;box-sizing:border-box";
    text.setAttribute("aria-label", "Generated integration source");
    const current = options();
    const config = {
      ...Object.fromEntries(
        Object.entries(current).filter(
          ([key, value]) =>
            ["string", "boolean", "number"].includes(typeof value) &&
            !/password|token|secret|credential|headers|authorization|cookie|key|url/i.test(
              key,
            ),
        ),
      ),
      ...(current.editor ? { editor: current.editor } : {}),
      ...(current.theme ? { theme: current.theme } : {}),
      ...(current.permissions ? { permissions: current.permissions } : {}),
      ...(current.services?.editorPresentation
        ? {
            services: {
              editorPresentation: current.services.editorPresentation,
            },
          }
        : {}),
    };
    text.value = `${integrationMarkup(element, "integration")}\n<script type="module">\nimport ${JSON.stringify(integrationModuleUrl(element))};\ndocument.getElementById("integration").options = ${JSON.stringify(config, null, 2).replaceAll("<", "\\u003c")};\n// Supply authenticated data and host service callbacks separately.\n</script>`;
    const close = document.createElement("button");
    close.textContent = "Close source";
    close.onclick = () => source.close();
    source.addEventListener("close", () => source.remove(), { once: true });
    source.append(text, close);
    shadow.append(source);
    source.showModal();
  });
  button("Close configuration", () => dialog.close());
  gear.onclick = () => dialog.showModal();
  header.append(gear, dialog);
  shadow.append(header);
}
